import React, { useEffect, useLayoutEffect, useImperativeHandle, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { Col, Form, InputGroup, ListGroup, Offcanvas, Row } from 'react-bootstrap'
import { FieldArray, FieldArrayRenderProps, useField, useFormikContext } from 'formik'
import * as Yup from 'yup'

import { ArticleType } from '../../common/types'
import {
  isPositionRequired,
  isRelationTypeSameArticle,
  isSizedArticleType,
  nullishValueToInternalValue,
  nullishValueToValue,
  toPosDisplayValue,
  toRightPosValue
} from '..'
import { modifyArea } from '../articleAPI'
import { useFormikReset, DirtyEffect } from '../CommonComponents'
import { ArticleNameTitle } from '../ArticleNameTitle'
import { Area, Article, Layoutable } from '../types'

const createSimpleValidation =
  (schema: Yup.Schema, transform?: (value: unknown) => unknown) =>
  (value: unknown): string | undefined => {
    try {
      const argValue = transform !== undefined ? transform(value) : value
      schema.validateSync(argValue)
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.log(err)
      }
      return (err as Error).message
    }
  }

const areaXFromRightErrorMessage = '右から1以上にしてください'
const areaXFromLeftErrorMessage = '左から1以上にしてください'
const areaYErrorMessage = '1以上にしてください'

type FormArea = Omit<Area, 'y' | 'x' | 'height' | 'width'> & {
  y: string | number
  x: string | number
  height: string | number
  width: string | number
}

interface FormValues {
  areas: FormArea[]
}

type EventHandler = {
  onSubmit: (values: FormValues) => Promise<unknown>
}

const NoArticleArea = () => {
  return <>領域なし</>
}

interface ArticleStartPositionFieldProps {
  index: number
  inputDisabled: boolean
  isPositionRequired: boolean
  layoutable: Layoutable
}

const ArticleStartPositionField: React.FC<ArticleStartPositionFieldProps> = ({
  index,
  inputDisabled,
  isPositionRequired,
  layoutable
}) => {
  const [areaField] = useField<FormArea>({ name: `areas.${index}` })
  const [fromLeftField, fromLeftMeta] = useField<boolean>({
    name: `areas.${index}.fromLeft`,
    validate: createSimpleValidation(Yup.boolean().required())
  })
  const schemaSeed = ((s) => (isPositionRequired ? s.required() : s.nullable()))(
    Yup.number().transform((v) => (isNaN(parseFloat(v)) ? undefined : v))
  )
  const [yField, yMeta, yHelper] = useField<string | number>({
    name: `areas.${index}.y`,
    validate: createSimpleValidation(schemaSeed.min(0, areaYErrorMessage))
  })
  const [xField, xMeta, xHelper] = useField<string | number>({
    name: `areas.${index}.x`,
    validate: (value: string | number) => {
      if (fromLeftField.value) {
        const numValue = parseFloat(String(value))
        if (isNaN(numValue)) {
          return isPositionRequired ? areaXFromLeftErrorMessage : undefined
        }
        const display = layoutable.numberOfRows - numValue - Number(areaField.value.width) + 1
        return display < 1 ? areaXFromLeftErrorMessage : undefined
      }
      return createSimpleValidation(schemaSeed.min(0, areaXFromRightErrorMessage))(value)
    }
  })

  const fromLeftDirty = fromLeftField.value !== fromLeftMeta.initialValue
  const yDirty = yField.value !== yMeta.initialValue
  const xDirty = xField.value !== xMeta.initialValue

  const onYChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    yHelper.setValue(nullishValueToInternalValue(e.target.value))
  }

  const onXChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { numberOfRows } = layoutable
    const internalValue = nullishValueToInternalValue(e.target.value)
    const x = fromLeftField.value
      ? toRightPosValue(numberOfRows, areaField.value.width as number, internalValue)
      : internalValue
    xHelper.setValue(x as string | number)
  }

  return (
    <Row>
      <Form.Group as={Col} className='mb-2'>
        <Form.Label column>記事開始位置</Form.Label>
        <Row>
          <Form.Group as={Col}>
            <DirtyEffect dirty={yDirty}>
              <InputGroup size='sm' hasValidation>
                <InputGroup.Text>天から</InputGroup.Text>
                <Form.Control
                  type='number'
                  min={0}
                  placeholder='ナリ'
                  {...yField}
                  value={nullishValueToValue(yField.value)}
                  disabled={inputDisabled}
                  isInvalid={!!yMeta.error}
                  onChange={onYChange}
                />
                <InputGroup.Text>段目</InputGroup.Text>
                <Form.Control.Feedback type='invalid'>{yMeta.error}</Form.Control.Feedback>
              </InputGroup>
            </DirtyEffect>
          </Form.Group>
          <Form.Group as={Col}>
            <DirtyEffect dirty={xDirty || fromLeftDirty}>
              <InputGroup size='sm' hasValidation>
                <InputGroup.Text>{!fromLeftField.value ? '右' : '左'}から</InputGroup.Text>
                <Form.Control
                  type='number'
                  min={0}
                  placeholder='ナリ'
                  {...xField}
                  value={toPosDisplayValue(layoutable, areaField.value)}
                  onChange={onXChange}
                  disabled={inputDisabled}
                  isInvalid={!!xMeta.error}
                />
                <InputGroup.Text>行目</InputGroup.Text>
                <Form.Control.Feedback type='invalid'>{xMeta.error}</Form.Control.Feedback>
              </InputGroup>
              <Form.Check
                type='switch'
                id='fromLeftSwitch'
                label='左から'
                name={fromLeftField.name}
                onChange={fromLeftField.onChange}
                onBlur={fromLeftField.onBlur}
                checked={fromLeftField.value}
                disabled={inputDisabled}
              />
            </DirtyEffect>
          </Form.Group>
        </Row>
      </Form.Group>
    </Row>
  )
}

interface ArticleSizeFieldProps {
  index: number
  inputDisabled: boolean
}

const sizeErrorMessage = '1以上にしてください'

const ArticleSizeField: React.FC<ArticleSizeFieldProps> = ({ index, inputDisabled }) => {
  const baseSchema = ((s) => s.required())(Yup.number().transform((v) => (isNaN(parseFloat(v)) ? undefined : v)))
  const [heightField, heightMeta] = useField<string | number>({
    name: `areas.${index}.height`,
    validate: createSimpleValidation(baseSchema.min(1, sizeErrorMessage))
  })
  const [widthField, widthMeta, widthHelper] = useField<string | number>({
    name: `areas.${index}.width`,
    validate: createSimpleValidation(baseSchema.min(1, sizeErrorMessage))
  })
  const [areaField] = useField<FormArea>({ name: `areas.${index}` })
  const [fromLeftField] = useField<boolean>({ name: `areas.${index}.fromLeft` })
  const [, , xHelper] = useField<string | number>({ name: `areas.${index}.x` })

  const cachedX = useRef<string | number | null | undefined>(undefined)
  const cachedWidth = useRef<number | null>(null)

  const heightDirty = heightField.value !== heightMeta.initialValue
  const widthDirty = widthField.value !== widthMeta.initialValue

  const onWidthFocus = () => {
    cachedX.current = areaField.value.x
    cachedWidth.current = parseInt(String(widthField.value))
  }

  const onWidthChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newWidth = parseInt(e.target.value)
    const oldWidth = cachedWidth.current
    widthHelper.setValue(newWidth || '')
    if (
      fromLeftField.value &&
      cachedX.current != null &&
      cachedX.current !== '' &&
      oldWidth !== null &&
      !isNaN(oldWidth) &&
      !isNaN(newWidth)
    ) {
      xHelper.setValue(Number(cachedX.current) + oldWidth - newWidth)
    }
  }

  return (
    <Row>
      <Form.Group as={Col} className='mb-2'>
        <Form.Label column>記事サイズ</Form.Label>
        <Row>
          <Form.Group as={Col}>
            <DirtyEffect dirty={heightDirty}>
              <InputGroup size='sm' hasValidation>
                <InputGroup.Text>天地</InputGroup.Text>
                <Form.Control
                  type='number'
                  min={1}
                  {...heightField}
                  value={parseInt(String(heightField.value))}
                  disabled={inputDisabled}
                  isInvalid={!!heightMeta.error}
                />
                <InputGroup.Text>段</InputGroup.Text>
                <Form.Control.Feedback type='invalid'>{heightMeta.error}</Form.Control.Feedback>
              </InputGroup>
            </DirtyEffect>
          </Form.Group>
          <Form.Group as={Col}>
            <DirtyEffect dirty={widthDirty}>
              <InputGroup size='sm' hasValidation>
                <InputGroup.Text>左右</InputGroup.Text>
                <Form.Control
                  type='number'
                  min={1}
                  {...widthField}
                  value={parseInt(String(widthField.value))}
                  onFocus={onWidthFocus}
                  onChange={onWidthChange}
                  disabled={inputDisabled}
                  isInvalid={!!widthMeta.error}
                />
                <InputGroup.Text>行幅</InputGroup.Text>
                <Form.Control.Feedback type='invalid'>{widthMeta.error}</Form.Control.Feedback>
              </InputGroup>
            </DirtyEffect>
          </Form.Group>
        </Row>
      </Form.Group>
    </Row>
  )
}

interface AreaFieldProps {
  index: number
  inputDisabled: boolean
  isPositionRequired: boolean
  layoutable: Layoutable
}

const AreaField: React.FC<AreaFieldProps> = ({ index, inputDisabled, isPositionRequired, layoutable }) => {
  useField(`areas.${index}`)

  return (
    <>
      <ArticleStartPositionField
        index={index}
        inputDisabled={inputDisabled}
        isPositionRequired={isPositionRequired}
        layoutable={layoutable}
      />
      <ArticleSizeField index={index} inputDisabled={inputDisabled} />
    </>
  )
}

interface AreaFieldsProps {
  article: Article
  childrenArticles: Record<string, Article[]>
  inputDisabled: boolean
  layoutable: Layoutable
  arrayHelpers: FieldArrayRenderProps
}

const AreaFields: React.FC<AreaFieldsProps> = ({ article, childrenArticles, inputDisabled, layoutable }) => {
  const context = useFormikContext<FormValues>()
  const areas = context.values.areas || []

  const commonProps = {
    isPositionRequired: isPositionRequired(article.articleType),
    inputDisabled,
    layoutable
  }

  const showAreaFields =
    !isSizedArticleType(article.articleType) &&
    !isRelationTypeSameArticle(childrenArticles, article) &&
    article?.articleType !== ArticleType.Flow &&
    article?.areas?.length

  if (!showAreaFields) {
    return <NoArticleArea />
  }

  const areaFields = areas.map((area, index) => {
    return <AreaField key={area.id ?? index} index={index} {...commonProps} />
  })

  return <ListGroup>{areaFields}</ListGroup>
}

interface ArticleAreasDetailBodyProps {
  article: Article
  childrenArticles: Record<string, Article[]>
  inputDisabled: boolean
  layoutable: Layoutable
}

const ArticleAreasDetailBody: React.FC<ArticleAreasDetailBodyProps> = (props) => {
  return (
    <>
      <FieldArray name='areas'>{(arrayHelpers) => <AreaFields arrayHelpers={arrayHelpers} {...props} />}</FieldArray>
    </>
  )
}

interface Props {
  article: Article
  childrenArticles: Record<string, Article[]>
  eventHandlerRef: React.RefObject<EventHandler | null>
  inputDisabled: boolean
  layoutable: Layoutable
  layoutableId: string | number
  layoutableType: string
}

export const ArticleAreasDetail: React.FC<Props> = ({
  article,
  childrenArticles,
  eventHandlerRef,
  inputDisabled,
  layoutable,
  layoutableId,
  layoutableType
}) => {
  const commonProps: ArticleAreasDetailBodyProps = {
    article,
    childrenArticles,
    inputDisabled,
    layoutable
  }

  const context = useFormikContext<Partial<FormValues>>()
  const formikReset = useFormikReset()
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, UnknownAction>>()

  useEffect(() => {
    const initialValues: FormValues = {
      areas: (article.areas ?? []).map((area) => {
        return {
          ...area,
          y: area.y ? area.y : '',
          x: area.x ? area.x : '',
          height: area.height ? area.height : '',
          width: area.width ? area.width : ''
        }
      })
    }

    context.resetForm({ values: initialValues })

    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [])

  const focusElementRef = useRef<HTMLSpanElement>(null)

  useImperativeHandle(eventHandlerRef, () => ({
    onSubmit: async (values: FormValues) => {
      const area = values.areas?.[0]
      return dispatch(
        modifyArea({
          layoutableType,
          layoutableId,
          layoutableLockVersion: layoutable?.lockVersion,
          area
        } as Parameters<typeof modifyArea>[0])
      ).then(() => {
        formikReset.resetForm({ values: context.values })
      })
    }
  }))

  useLayoutEffect(() => {
    const timeoutId = setTimeout(() => {
      focusElementRef.current?.focus()
      focusElementRef.current?.blur()
    }, 2)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [context.initialValues])

  if ((context.values as Partial<FormValues> | undefined)?.areas === undefined) {
    return undefined
  }

  return (
    <>
      <Offcanvas.Header>
        <Offcanvas.Title className='detail-offcanvas-title'>
          領域
          <ArticleNameTitle article={article} />
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className='article-areas-detail-offcanvas-body'>
        <ArticleAreasDetailBody {...commonProps} />
      </Offcanvas.Body>
    </>
  )
}
